import { CommonModule } from '@angular/common';
import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { type SheetFunctions } from '@vanguard/axs-components';
import { ContentService } from 'src/app/shared/services/content/content.service';
import { content } from 'src/assets/content/fallback-content';

import { GatekeeperService } from '../../shared/services/gatekeeper/gatekeeper.service';

@Component({
  selector: 'terminology-sheet',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './terminology-sheet.component.html',
  styleUrl: './terminology-sheet.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  encapsulation: ViewEncapsulation.None,
})
export class TerminologySheetComponent implements OnInit {
  @ViewChild('sheetRef') sheetRef: any;

  calculatorInputsContent: any = content.Data.v4_rcc_root.content.calculatorInputs;
  calculatorResultsContent = content.Data.v4_rcc_root.content.calculatorResults;
  needHelpContent = content.Data.v4_rcc_root.content.needHelp;
  highlightedField: string | null = null;
  stateTaxRatesFromBXR = false;
  accordionOpenStatus: Record<string, boolean> = {
    clientTax: false,
    rothConversion: false,
    profitabilityPoint: false,
    breakEvenTaxRate: false,
  };

  readonly ACCORDION_KEY_ID: Record<string, string> = {
    clientTax: 'sheet-accordion-client-tax',
    rothConversion: 'sheet-accordion-roth-conversion',
    profitabilityPoint: 'sheet-accordion-profitability-point',
    breakEvenTaxRate: 'sheet-accordion-break-even-tax-rate',
  };

  private readonly SCROLL_MARGIN_CONFIG: Record<string, string> = {
    'sheet-field-annual-taxable-income': '102px',
    'sheet-field-years-until': '66px',
    'sheet-field-future-taxable-income': '50px',
    'tax-considerations-content': '66px',
  };
  private readonly DEFAULT_SCROLL_MARGIN = '20px';

  constructor(
    private readonly contentService: ContentService,
    private readonly gatekeeperService: GatekeeperService,
  ) {}

  ngOnInit(): void {
    this.stateTaxRatesFromBXR = this.gatekeeperService.getToggleValue('STATE_TAX_RATES');
    this.contentService.getContent().subscribe((data) => {
      this.calculatorInputsContent = data.Data.v4_rcc_root.content.calculatorInputs;
      this.calculatorResultsContent = data.Data.v4_rcc_root.content.calculatorResults;
      this.needHelpContent = data.Data.v4_rcc_root.content.needHelp;
    });
  }

  openSheet(): void {
    if (!this.isSheetOpen()) {
      (this.sheetRef.nativeElement as unknown as SheetFunctions).show();
    }
  }

  onClose() {
    this.resetToDefaultState();
  }

  openWithHighlight(
    accordionKey: string | null,
    fieldId: string,
    highlightOverride?: string,
  ): void {
    this.openSheet();

    this.highlightedField = highlightOverride ?? fieldId;

    if (accordionKey) {
      const panel = document.getElementById(this.ACCORDION_KEY_ID[accordionKey]) as HTMLElement;
      const target = document.getElementById(fieldId) as HTMLElement;
      requestAnimationFrame(() => {
        panel.addEventListener('axs-accordion-opened', () => this.scrollToTarget(target, fieldId), {
          once: true,
        });
        requestAnimationFrame(() => {
          this.accordionOpenStatus[accordionKey] = true;
        });
      });
    } else {
      const target = document.getElementById(fieldId) as HTMLElement;
      setTimeout(() => this.scrollToTarget(target, fieldId), 250);
    }
  }

  private isSheetOpen(): boolean {
    const dialog = this.sheetRef?.nativeElement?.shadowRoot?.querySelector('dialog');
    return Boolean(dialog?.open);
  }

  private scrollToTarget(target: HTMLElement, fieldId: string): void {
    target.style.scrollMarginTop = this.SCROLL_MARGIN_CONFIG[fieldId] || this.DEFAULT_SCROLL_MARGIN;
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });

    setTimeout(() => {
      target.style.scrollMarginTop = '';
    }, 100);
  }

  onAccordionToggle(key: string, isOpen: boolean): void {
    this.accordionOpenStatus[key] = isOpen;
  }

  closeAccordions(): void {
    this.accordionOpenStatus = {
      clientTax: false,
      rothConversion: false,
      profitabilityPoint: false,
      breakEvenTaxRate: false,
    };
  }

  private resetToDefaultState(): void {
    this.highlightedField = null;
    this.closeAccordions();
    this.scrollToTop();
  }

  private scrollToTop(): void {
    const dialog = this.sheetRef?.nativeElement?.shadowRoot?.querySelector('dialog');
    if (dialog) {
      dialog.scrollTop = 0;
    }

    const contentSlot = this.sheetRef?.nativeElement?.querySelector('[slot="content"]');
    if (contentSlot) {
      contentSlot.scrollTop = 0;
    }

    setTimeout(() => {
      const contentWrapper = this.sheetRef?.nativeElement?.querySelector('.sheet');
      if (contentWrapper) {
        contentWrapper.scrollIntoView({ behavior: 'auto', block: 'start' });
      }
    }, 50);
  }
}
